import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader, StatusBadge, Loader, EmptyState } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { Mail, Send, Bot, PenLine, ArrowRight, Sparkles, KeyRound } from "lucide-react";

export default function DigestPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [email, setEmail] = useState("");
  const [key, setKey] = useState("");
  const [sending, setSending] = useState(false);
  const nav = useNavigate();

  const load = () => api.get("/digest").then((r) => { setData(r.data); setEmail(r.data.digest_email || ""); }).catch(() => setErr(true));
  useEffect(() => { load(); }, []);

  const toggle = async (v) => {
    setData((d) => ({ ...d, digest_enabled: v }));
    await api.post("/digest/settings", { digest_enabled: v });
    toast.success(v ? "Daily digest ON" : "Daily digest paused");
  };
  const saveSettings = async () => {
    if (!email.trim()) { toast.error("Enter your email"); return; }
    await api.post("/digest/settings", { digest_email: email, resend_api_key: key || undefined });
    toast.success("Digest settings saved"); setKey(""); load();
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/digest/send");
      if (r.data.sent) toast.success(`Digest emailed to ${r.data.to}`);
      else toast.error(r.data.reason || "Could not send");
    } catch (e) { toast.error("Send failed"); }
    setSending(false);
    load();
  };

  if (err) return <div className="p-6 lg:p-10" data-testid="digest-error"><EmptyState icon={Mail} title="Couldn't load your digest" hint="Check your connection and refresh the page." /></div>;
  if (!data) return <div className="p-6 lg:p-10"><Loader label="Building your digest" /></div>;

  return (
    <div className="p-6 lg:p-10 max-w-5xl">
      <PageHeader eyebrow="Approve & Upload" title="Daily Digest" testid="digest-header"
        subtitle="A morning summary of everything your studio produced — review it, then upload in one sitting." />

      <div className="bg-surface border border-white/10 rounded-md p-5 mb-6 fade-up">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center border ${data.digest_enabled ? "bg-brand/15 border-brand/40" : "bg-white/5 border-white/10"}`}>
              <Mail className={`w-5 h-5 ${data.digest_enabled ? "text-brand" : "text-white/40"}`} />
            </div>
            <div>
              <h3 className="font-heading text-lg">Email me every morning</h3>
              <p className="text-white/40 text-sm">Sent daily at 13:00 UTC{data.digest_email ? ` to ${data.digest_email}` : ""}.</p>
            </div>
          </div>
          <Switch data-testid="digest-toggle" checked={!!data.digest_enabled} onCheckedChange={toggle} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono">Your Email</label>
            <input data-testid="digest-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 outline-none focus:border-brand text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-white/40 mb-2 block font-mono flex items-center gap-1"><KeyRound className="w-3 h-3" /> Resend API Key {data.resend_key_set && <span className="text-signal-green">· set</span>}</label>
            <input data-testid="resend-key-input" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder={data.resend_key_set ? "•••••• (saved)" : "re_..."}
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-4 py-2.5 outline-none focus:border-brand text-sm" />
          </div>
        </div>
        <p className="text-[11px] text-white/30 mt-2 font-mono">Get a free key at resend.com. On the free tier, emails send from onboarding@resend.dev to your own verified address.</p>
        <div className="flex gap-2 mt-4">
          <button data-testid="save-digest-btn" onClick={saveSettings} className="bg-white/10 hover:bg-white/15 rounded-md px-5 py-2.5 text-sm font-medium transition-colors">Save</button>
          <button data-testid="send-digest-btn" onClick={sendNow} disabled={sending} className="bg-brand hover:bg-brand-hover disabled:opacity-50 text-white rounded-md px-5 py-2.5 text-sm font-medium transition-colors flex items-center gap-2">
            <Send className="w-4 h-4" /> {sending ? "Sending..." : "Send Test Digest"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-6 fade-up">
        <div className="bg-surface border border-white/10 rounded-md p-5 text-center">
          <div className="font-mono text-3xl">{data.package_count}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/30 font-mono mt-1">Videos Today</div>
        </div>
        <div className="bg-surface border border-white/10 rounded-md p-5 text-center">
          <div className="font-mono text-3xl text-brand">{data.autopilot_count}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/30 font-mono mt-1">By Autopilot</div>
        </div>
        <div className="bg-surface border border-white/10 rounded-md p-5 text-center">
          <div className="font-mono text-3xl text-signal-yellow">{data.trends_today}</div>
          <div className="text-[10px] uppercase tracking-wider text-white/30 font-mono mt-1">Trends Found</div>
        </div>
      </div>

      <div className="bg-surface border border-white/10 rounded-md p-5 fade-up">
        <h3 className="font-heading text-xl tracking-tight mb-4 flex items-center gap-2"><Sparkles className="w-4 h-4 text-brand" /> Ready to approve & upload</h3>
        {data.packages.length === 0 ? (
          <EmptyState icon={Mail} title="Nothing produced yet today" hint="Autopilot runs every 6 hours, or hit 'Run Now' in Settings to generate videos instantly." />
        ) : (
          <div className="space-y-2">
            {data.packages.map((p) => (
              <div key={p.id} onClick={() => nav("/library")} data-testid={`digest-item-${p.id}`}
                className="flex items-center gap-4 bg-[#0A0A0A] border border-white/5 rounded px-4 py-3 hover:border-white/20 transition-colors cursor-pointer group">
                {p.source === "autopilot" ? <Bot className="w-4 h-4 text-brand shrink-0" /> : <PenLine className="w-4 h-4 text-white/40 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="text-sm truncate group-hover:text-brand transition-colors">{p.title}</div>
                  <div className="text-[10px] font-mono text-white/30 uppercase tracking-wider mt-0.5">{p.source} · {p.niche || "general"}</div>
                </div>
                <StatusBadge status={p.status} />
                <ArrowRight className="w-4 h-4 text-white/20 group-hover:text-white transition-colors shrink-0" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
